import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Search, TrendingUp, BarChart3, Target, Home, Zap, Eye, MousePointer } from 'lucide-react';

const SeoSeaOptimierung = () => {
  return (
    <div className="page-container">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/"><Button variant="outline" size="sm"><Home className="h-4 w-4 mr-2" />Zurück zur Startseite</Button></Link>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">SEO & SEA Optimierung</span>
          </div>
        </div>
      </div>

      <section className="hero-section-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Banner Image */}
          <div className="mb-8 rounded-xl overflow-hidden" style={{maxHeight: '200px'}}>
            <img 
              src="https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg" 
              alt="SEO Marketing" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h1 className="service-page-title">SEO & SEA Optimierung – Google Ads, Bing Ads, Suchmaschinenoptimierung</h1>
              <p className="service-page-subtitle">Von Google Ads über Bing Ads bis zur technischen Suchmaschinenoptimierung – ich maximiere Ihre Online-Sichtbarkeit und senke gleichzeitig die Werbekosten. Datengetriebene Kampagnen mit messbarem ROI.</p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/#kontakt"><Button size="lg" className="cta-button-large">Kostenlose Erstanalyse<ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-accent" /><span>Mehr Sichtbarkeit</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-accent" /><span>Weniger Werbekosten</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-accent" /><span>Messbare Ergebnisse</span></div>
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">SEO & SEA Leistungen</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><Search className="h-6 w-6" /></div><h3 className="service-title">Google Ads Optimierung</h3><p className="service-description">Kampagnen-Setup, Keyword-Recherche, Gebotsstrategien und Conversion-Tracking für maximalen ROI.</p></CardContent></Card>
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><Eye className="h-6 w-6" /></div><h3 className="service-title">Technisches SEO</h3><p className="service-description">Website-Struktur, Ladegeschwindigkeit, Mobile-Optimierung und Schema-Markup für besseres Ranking.</p></CardContent></Card>
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><BarChart3 className="h-6 w-6" /></div><h3 className="service-title">Performance-Tracking</h3><p className="service-description">Google Analytics, Search Console, Conversion-Monitoring und ROI-Analyse.</p></CardContent></Card>
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><MousePointer className="h-6 w-6" /></div><h3 className="service-title">Bing Ads Management</h3><p className="service-description">Kampagnen-Optimierung für Microsoft Advertising mit Fokus auf B2B-Zielgruppen.</p></CardContent></Card>
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><TrendingUp className="h-6 w-6" /></div><h3 className="service-title">Content-Optimierung</h3><p className="service-description">Keyword-Strategie, On-Page SEO, Meta-Tags und strukturierte Inhalte für Top-Rankings.</p></CardContent></Card>
            <Card className="service-card"><CardContent className="p-6"><div className="service-icon"><Zap className="h-6 w-6" /></div><h3 className="service-title">Conversion-Optimierung</h3><p className="service-description">Landing Pages, A/B-Testing, Call-to-Actions und User Experience Verbesserung.</p></CardContent></Card>
          </div>
        </div>
      </section>

      <section className="visual-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-dark">Mehr Sichtbarkeit, weniger Kosten</h2>
          <div className="text-6xl font-bold text-accent my-8">-40%</div>
          <p className="text-xl text-gray-300">Durchschnittliche Reduzierung der Werbekosten bei gleichzeitig höherer Conversion-Rate durch professionelle SEO/SEA-Optimierung.</p>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Bereit für mehr Online-Sichtbarkeit?</h2>
          <p className="section-subtitle mb-8">Lassen Sie uns Ihre Online-Marketing-Strategie auf das nächste Level bringen.</p>
          <Link to="/#kontakt"><Button size="lg" className="cta-button-large">Jetzt kostenlose Erstanalyse anfragen<ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
        </div>
      </section>

      <footer className="footer"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"><p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.</p></div></footer>
    </div>
  );
};

export default SeoSeaOptimierung;